import { lazy, ReactNode, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Loadable from "../components/Loadable";
import RouteGuard from "../components/RouteGuard/RouteGuard";
import { CUSTOMER } from "../config/constants";
import HODashboardLayout from "../layouts/dashboardsLayouts/HODashboardLayout";
import Profile from "../pages/Profile/Profile";
import { IAuthState, resetLoggedOut, tryConfirmLogin } from "../stateManagement/reducers/authReducer";
import { IRootReducerT } from "../stateManagement/reduxStore";

interface IPageTemplateProps {
    altRoute: string;
    page: ReactNode;
    pageName: string;
}

const PageTemplate: React.FC<IPageTemplateProps> = ({ pageName, altRoute, page }) => {

    const dispatch = useDispatch<any>();
    const { authToken, user, loggedOut } = useSelector<IRootReducerT, IAuthState>(state => state.auth);
    let conditions: boolean[] = [];
    const hasValidAuthToken = (authToken !== null);
    const isTime4Email2FA = hasValidAuthToken && (user?.usesEmail2FA === true) && (user.email2FADone === false);
    const isTime4Authenticator2FA = hasValidAuthToken && !isTime4Email2FA && (user?.usesAuthenticator2FA === true) && (user.authenticator2FADone === false);
    const isCustomer = (user && (user?.role?.name === CUSTOMER)) ? true : false;

    const isAuthenticated = hasValidAuthToken && !isTime4Email2FA && !isTime4Authenticator2FA;

    switch (pageName) {
        case 'dashboard':
            conditions = [isAuthenticated, !isCustomer];
            break;
        default:
            conditions = [isAuthenticated, !isCustomer]     //User must be authenticated to access this route
            break;
    }

    const [isInitRender120, setIsInitRender120] = useState(true);
    useEffect(() => {
        if (isInitRender120) {
            setIsInitRender120(false);
            dispatch(resetLoggedOut({ authToken: authToken ?? '' }));
            return;
        }


        if (loggedOut) {
            window.open('/', '_self');
        }
    }, [loggedOut]);

    useEffect(() => {
        if (!user && authToken) {
            dispatch(tryConfirmLogin({ authToken }));
        }
    }, [authToken]);

    return (
        <>
            <RouteGuard conditions={conditions} altRoute={altRoute} />
            {page}
        </>
    );
}

//lazy pages
const LHODashboard = Loadable(lazy(() => import('../pages/dashboards/HODashboard')));
const LAdmins = Loadable(lazy(() => import('../pages/Admins/Admins')));
const LTutors = Loadable(lazy(() => import('../pages/Authors/Authors')));
const LCustomers = Loadable(lazy(() => import('../pages/Customers/Customers')));
const LProducts = Loadable(lazy(() => import('../pages/Products/Products')));
const LCourses = Loadable(lazy(() => import('../pages/Courses/Courses')));
const LArticles = Loadable(lazy(() => import('../pages/Articles/Articles')));
const LRecorededWorkshops = Loadable(lazy(() => import('../pages/RecordedWorkshops/RecordedWorkshops')));
const LEvents = Loadable(lazy(() => import('../pages/Events/Events')));



const LProfile = Loadable(lazy(() => import('../pages/Profile/Profile')));

const DashboardRoutes = {
    path: "/",
    element: <PageTemplate pageName="" altRoute={'/welcome'} page={<HODashboardLayout />} />,
    children: [
        {
            path: "dashboard",
            element: <PageTemplate pageName="dashboard" altRoute={'/welcome'} page={<LHODashboard />} />
        },
        {
            path: "dashboard/admins",
            element: <PageTemplate pageName="dashboard/admins" altRoute={'/welcome'} page={<LAdmins />} />
        },
        {
            path: "dashboard/authors",
            element: <PageTemplate pageName="dashboard/authors" altRoute={'/welcome'} page={<LTutors />} />
        },
        {
            path: "dashboard/customers",
            element: <PageTemplate pageName="dashboard/customers" altRoute={'/welcome'} page={<LCustomers />} />
        },
        {
            path: "dashboard/products",
            element: <PageTemplate pageName="dashboard/products" altRoute={'/welcome'} page={<LProducts />} />
        },
        {
            path: "dashboard/courses",
            element: <PageTemplate pageName="dashboard/courses" altRoute={'/welcome'} page={<LCourses />} />
        },
        {
            path: "dashboard/articles",
            element: <PageTemplate pageName="dashboard/articles" altRoute={'/welcome'} page={<LArticles />} />
        },
        {
            path: "dashboard/recorded-workshops",
            element: <PageTemplate pageName="dashboard/recorded-workshops" altRoute={'/welcome'} page={<LRecorededWorkshops />} />
        },
        {
            path: "dashboard/events",
            element: <PageTemplate pageName="dashboard/events" altRoute={'/welcome'} page={<LEvents />} />
        },
        {
            path: "profile",
            element: <Profile />
        }
    ]
};

export default DashboardRoutes;